import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import PeekAnswersContent from './PeekAnswersContent';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import TrueImpactCapsule from 'shared/design-system/components/TrueImpactCapsule';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DECORATORS } from 'shared/design-system/settings/variables/Decorators';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import {
  getReportsForProgram,
  ReportsForProgram,
} from '../../../ReportsBeta/NewReport/api';
import { mapReportPrograms } from '../../../ReportsBeta/NewReport/NewReportContent';
import { Report } from 'shared/components/WelcomeBack/utils';
import { Program } from '../../../Reports/NewReportDialog';

interface PeekAnswersProps {
  report: Report;
  program: Program;
}

const borderColor = COLORS.grayBase;
const StyledButton = styled.div`
  position: fixed;
  left: ${SPACERS.medium};
  bottom: ${SPACERS.medium};
  z-index: 999;
  .peek-capsule {
    display: flex;
    align-items: center;
    padding: 0;
    border-top: 1px solid ${borderColor};
    border-bottom: 1px solid ${borderColor};
    border-left: 1px solid ${borderColor};
    box-shadow: ${DECORATORS.boxShadowDefault};
  }
  .peek-text {
    padding: ${SPACERS.tiny} ${SPACERS.tiny} ${SPACERS.tiny} ${SPACERS.small};
  }
  .peek-close {
    cursor: pointer;
    border: 1px solid ${borderColor};
    border-radius: ${SPACERS.medium};
    padding: ${SPACERS.tiny} 10px;
    transition: background-color 0.5s;
    &:hover {
      background-color: ${COLORS.grayLight};
    }
  }
`;

const PeekAnswers: React.FC<PeekAnswersProps> = ({ report, program }) => {
  const client = useClient();
  const [show, setShow] = useState<boolean>(false);
  const [peek, setPeek] = useState<boolean>(false);
  const [programs, setPrograms] = useState<
    ReturnType<typeof mapReportPrograms>
  >();
  const [reportsForProgram, makeReportsForProgramRequest] = useGraphQLRequest<{
    reports: ReportsForProgram[];
  }>();

  useEffect(() => {
    if (reportsForProgram.type === 'REQUEST_SUCCESS') {
      setPrograms(mapReportPrograms(reportsForProgram.data?.reports));
    }
  }, [reportsForProgram]);

  useEffect(() => {
    if (!client) return;
    makeReportsForProgramRequest(getReportsForProgram(client));
  }, []);

  const handleOpen = useCallback(() => {
    setPeek(true);
  }, [setPeek]);

  const handleClose = useCallback(() => {
    setPeek(false);
  }, [setPeek]);

  const onClose = useCallback(() => {
    setShow(false);
  }, [setShow]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.altKey &&
        event.code === 'KeyP'
      ) {
        setShow(o => !o);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShow]);

  if (!show || !programs) return null;
  return (
    <>
      <StyledButton>
        <TrueImpactCapsule className="peek-capsule">
          <TrueImpactText
            variant="body-small-bold"
            className="peek-text"
            onClick={handleOpen}
          >
            Peek Reports
          </TrueImpactText>
          <FontAwesomeIcon
            icon={faXmark}
            className="peek-close"
            onClick={onClose}
          />
        </TrueImpactCapsule>
      </StyledButton>
      <TrueImpactModal
        open={peek}
        hideFooter={true}
        showExpandButton={true}
        onClose={handleClose}
      >
        <PeekAnswersContent programs={programs} currentProgram={program} />
      </TrueImpactModal>
    </>
  );
};

export default PeekAnswers;
